import { QueryTabs, WorkbenchOutputNames } from 'types/query-result';
import { debugLog } from 'utils/debug';

let jsonexport: (data: object[], options: any, callback: (error: Error, results: string) => void) => void;

// Dynamic import of `jsonexport` is required to prevent the storybook build from failing
(async () => {
  try {
    const module = await import('jsonexport');
    jsonexport = module.default;
  } catch (error) {
    debugLog('Failed to load jsonexport module:', error);
  }
})();

export const getCopyTooltip = (outputSelected: WorkbenchOutputNames) => {
  switch (outputSelected) {
    case QueryTabs.Table:
      return 'Copy results as TSV.';
    case QueryTabs.JSON:
      return 'Copy results as JSON.';
    case QueryTabs.PLAN:
    case QueryTabs.PLAN_TEXT:
      return 'Copy query plan text.';
    default:
      return '';
  }
};

export const flattenObject = (obj: any, parentKey = ''): Record<string, unknown> => {
  return Object.keys(obj).reduce((acc, key) => {
    const currentKey = parentKey ? `${parentKey}.${key}` : key;
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      return { ...acc, ...flattenObject(obj[key], currentKey) };
    }
    return { ...acc, [currentKey]: obj[key] };
  }, {});
};

export const stringifyResult = (jsonObject: Record<string, unknown> | Record<string, unknown>[]) => {
  if (!jsonObject || (Array.isArray(jsonObject) && jsonObject.length === 0)) {
    return JSON.stringify(jsonObject, null, 2);
  }

  if (!Array.isArray(jsonObject)) {
    return JSON.stringify(jsonObject, null, 2);
  }

  // Incrementally build the JSON string for large arrays with proper indentation
  let str = '[\n';
  for (let i = 0; i < jsonObject.length; i++) {
    str += `  ${JSON.stringify(jsonObject[i], null, 2).replace(/\n/g, '\n  ')}`;
    if (i < jsonObject.length - 1) {
      str += ',\n';
    }
  }
  str += '\n]';
  return str;
};

export const convertJsonToTSV = (tableResults: object[]) => {
  if (!jsonexport) {
    // If jsonexport is not available, return a promise rejection
    return Promise.reject(new Error('jsonexport is not available in the browser.'));
  }

  const flattenedData = tableResults.map((entry) => flattenObject(entry));
  const headerNames = [...new Set(flattenedData.flatMap((entry) => Object.keys(entry)))].sort();

  const options = {
    headers: headerNames,
    rowDelimiter: '\t',
  };

  return new Promise<string>((resolve, reject) => {
    jsonexport(tableResults, options, (error, results) => {
      if (error) {
        reject(error);
      } else {
        resolve(results);
      }
    });
  });
};

export const isBatchQueryResult = (data: any) => {
  const isObjectWithProperties = (row: any) => typeof row === 'object' && row !== null && 'batchQuery' in row && 'batchQueryResult' in row;

  return Array.isArray(data) && data.length !== 0 && data.every(isObjectWithProperties);
};
